// eslint-disable-next-line max-classes-per-file
import {
  Account,
  AccountAuthenticator,
  AccountAuthenticatorEd25519,
  Aptos,
  AptosConfig,
  deserializePublicKey,
  Ed25519PublicKey,
  Ed25519Signature,
  Network,
  PublicKey,
  SigningScheme,
} from '@aptos-labs/ts-sdk';
import {
  AccountInfo,
  AptosConnectMethod,
  AptosDisconnectMethod,
  AptosFeatures,
  AptosGetAccountMethod,
  AptosGetNetworkMethod,
  AptosOnAccountChangeMethod,
  AptosOnNetworkChangeMethod,
  AptosSignAndSubmitTransactionMethod,
  AptosSignMessageInput,
  AptosSignMessageMethod,
  AptosSignMessageOutput,
  AptosSignTransactionMethod,
  AptosWallet,
  AptosWalletAccount,
  IdentifierArray,
  NetworkInfo,
  UserResponse,
  UserResponseStatus,
} from '@aptos-labs/wallet-standard';
import { OKXAptosProvider } from '@okxconnect/aptos-provider';
import { OKXUniversalConnectUI, THEME } from '@okxconnect/ui';
import { isObject, isString } from 'lodash';
import { AptosUtils } from './aptos-utils';

export class MyWalletAccount implements AptosWalletAccount {
  address: string;

  publicKey: Uint8Array;

  chains: IdentifierArray = ['aptos:mainnet'];

  features: IdentifierArray;

  signingScheme: SigningScheme;

  label?: string;

  icon?:
    | `data:image/svg+xml;base64,${string}`
    | `data:image/webp;base64,${string}`
    | `data:image/png;base64,${string}`
    | `data:image/gif;base64,${string}`
    | undefined;

  constructor(account: Account) {
    this.address = account.accountAddress.toString();
    this.publicKey = account.publicKey.toUint8Array();
    this.features = ['aptos:connect'];
    this.signingScheme = SigningScheme.Ed25519;
  }
}

export const OKXWalletName = 'Offical OKX Wallet';

export class OfficialOKXWallet implements AptosWallet {
  readonly url = 'https://okx.com/web3/';

  readonly version = '1.0.0';

  readonly name = OKXWalletName;

  readonly icon =
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAYAAABXAvmHAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAJDSURBVHgB7Zq9jtpAEMfHlhEgQLiioXEkoAGECwoKxMcTRHmC5E3IoyRPkPAEkI7unJYmTgEFTYwA8a3NTKScLnCHN6c9r1e3P2llWQy7M/s1Gv1twCP0ej37dDq9x+Zut1t3t9vZjDEHIiSRSPg4ZpDL5fxkMvn1cDh8m0wmfugfO53OoFQq/crn8wxfY9EymQyrVCqMfHvScZx1p9ls3pFxXBy/bKlUipGPrVbLuQqAfsCliq3zl0H84zwtjQrOw4Mt1W63P5LvBm2d+Xz+YzqdgkqUy+WgWCy+Mc/nc282m4FqLBYL+3g8fjDxenq72WxANZbLJeA13zDX67UDioL5ybXwafMYu64Ltn3bdDweQ5R97fd7GyhBQMipx4POeEDHIu2LfDdBIGGz+hJ9CQ1ABjoA2egAZPM6AgiCAEQhsi/C4jHyPA/6/f5NG3Ks2+3CYDC4aTccDrn6ojG54MnEvG00GoVmWLIRNZ7wTCwDHYBsdACy0QHIhiuRETxlICWpMMhGZHmqS8qH6JLyGegAZKMDkI0uKf8X4SWlaZo+Pp1bRrwlJU8ZKLIvUjKh0WiQ3sRUbNVq9c5Ebew7KEo2m/1p4jJ4qAmDaqDQBzj5XyiAT4VCQezJigAU+IDU+z8vJFnGWeC+bKQV/5VZ71FV6L7PA3gg3tXrdQ+DgLhC+75Wq3no69P3MC0NFQpx2lL04Ql9gHK1bRDjsSBIvScBnDTk1WrlGIZBorIDEYJj+rhdgnQ67VmWRe0zlplXl81vcyEt0rSoYDUAAAAASUVORK5CYII=';

  readonly chains = ['aptos:mainnet'] as const;

  accounts: MyWalletAccount[] = [];

  readonly aptos: Aptos;

  constructor(apiKey?: string) {
    this.aptos = new Aptos(
      new AptosConfig({
        fullnode: 'https://api.mainnet.aptoslabs.com/v1',
        network: Network.MAINNET,
        clientConfig: {
          API_KEY: apiKey,
        },
      }),
    );
  }

  account: AptosGetAccountMethod = async (): Promise<AccountInfo> => {
    const connected = this.universalProvider?.connected();
    const response = this.provider?.getAccount('aptos:mainnet');

    if (!response || !connected) {
      throw new Error(`${OKXWalletName} Account Error`);
    }

    const account = new AccountInfo({
      address: response.address,
      publicKey: new Ed25519PublicKey(response.publicKey),
    });
    return Promise.resolve(account);
  };

  connectPromise: Promise<AccountInfo> | undefined;

  universalProvider: OKXUniversalConnectUI | undefined;

  provider: OKXAptosProvider | undefined;

  connect: AptosConnectMethod = async (): Promise<
    UserResponse<AccountInfo>
  > => {
    try {
      if (!this.connectPromise) {
        this.connectPromise = (async () => {
          this.universalProvider = await OKXUniversalConnectUI.init({
            dappMetaData: {
              name: 'Aries Markets',
              icon: 'https://app.ariesmarkets.xyz/favicon.ico',
            },
            actionsConfiguration: {
              returnStrategy: 'back',
              modals: 'all',
            },
            language: 'en_US',
            uiPreferences: {
              theme: THEME.DARK,
            },
          });

          await this.universalProvider.openModal({
            namespaces: {
              aptos: {
                chains: ['aptos:mainnet'],
              },
            },
            sessionConfig: {},
          });

          this.provider = new OKXAptosProvider(this.universalProvider);

          const addressInfo = this.provider.getAccount('aptos:mainnet');
          if (!addressInfo) {
            throw new Error(`${OKXWalletName} Address Info Error`);
          }

          let publicKey: PublicKey;
          try {
            publicKey = deserializePublicKey(addressInfo.publicKey);
          } catch (error) {
            publicKey = new Ed25519PublicKey(addressInfo?.publicKey);
          }

          if (!publicKey) {
            throw new Error(`${OKXWalletName} Address Info Error`);
          }

          return new AccountInfo({
            address: addressInfo.address,
            publicKey,
          });
        })();
      }

      const accountInfo = await this.connectPromise;

      setTimeout(() => {
        this.connectPromise = undefined;
      }, 2000);

      return {
        status: UserResponseStatus.APPROVED,
        args: accountInfo,
      };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('🚀 ~ OfficialOKXWallet ~ connect error:', e);
      setTimeout(() => {
        this.connectPromise = undefined;
      }, 2000);
      throw Error(`error connecting to wallet ${e}`);
    }
  };

  network: AptosGetNetworkMethod = async (): Promise<NetworkInfo> => {
    return {
      name: Network.MAINNET,
      chainId: 1,
    };
  };

  disconnect: AptosDisconnectMethod = async (): Promise<void> => {
    await this.universalProvider?.disconnect();
  };

  signAndSubmitTransaction: AptosSignAndSubmitTransactionMethod =
    async transaction => {
      const addressInfo = this.provider?.getAccount('aptos:mainnet');

      if (!addressInfo) {
        await this.universalProvider?.disconnect();

        throw new Error('No Wallet Connected');
      }

      const { payload } = transaction;

      if (!('function' in payload)) {
        throw new Error('Invalid Input');
      }

      const simpleTransaction = await this.aptos.transaction.build.simple({
        sender: addressInfo.address,
        data: {
          function: payload.function as `${string}::${string}::${string}`,
          typeArguments: payload.typeArguments,
          functionArguments: payload.functionArguments,
        },
      });

      const transactionDataInner = {
        arguments: payload.functionArguments,
        function: payload.function,
        type: 'entry_function_payload',
        type_arguments: payload.typeArguments || [],
      };

      const transactionData =
        payload.functionArguments[0] !== null
          ? AptosUtils.getTransaction(simpleTransaction)
          : transactionDataInner;

      const res = await this.provider?.signAndSubmitTransaction(
        transactionData,
        undefined as unknown as object,
        'aptos:mainnet',
      );

      if (!res) {
        throw new Error('No response');
      }

      if (!isString(res)) {
        throw res &&
          isObject(res) &&
          isString((res as { message: string }).message)
          ? new Error(
              (res as { message: string }).message || 'Unknown Error',
            )
          : new Error('Unknown Error');
      }

      return {
        status: UserResponseStatus.APPROVED,
        args: { hash: res },
      };
    };

  signTransaction: AptosSignTransactionMethod = async (): Promise<
    UserResponse<AccountAuthenticator>
  > => {
    return Promise.resolve({
      status: UserResponseStatus.APPROVED,
      args: new AccountAuthenticatorEd25519(
        new Ed25519PublicKey('0x'),
        new Ed25519Signature('0x'),
      ),
    });
  };

  signMessage: AptosSignMessageMethod = async (
    input: AptosSignMessageInput,
  ): Promise<UserResponse<AptosSignMessageOutput>> => {
    const { address } = await this.account();

    return Promise.resolve({
      status: UserResponseStatus.APPROVED,
      args: {
        address: address.toString(),
        fullMessage: '',
        message: input.message,
        nonce: input.nonce,
        prefix: 'APTOS',
        signature: new Ed25519Signature('0x'),
      },
    });
  };

  onAccountChange: AptosOnAccountChangeMethod =
    async (): Promise<void> => {
      return Promise.resolve();
    };

  onNetworkChange: AptosOnNetworkChangeMethod =
    async (): Promise<void> => {
      return Promise.resolve();
    };

  features: AptosFeatures = {
    'aptos:connect': {
      version: '1.0.0',
      connect: this.connect,
    },
    'aptos:network': {
      version: '1.0.0',
      network: this.network,
    },
    'aptos:disconnect': {
      version: '1.0.0',
      disconnect: this.disconnect,
    },
    'aptos:signTransaction': {
      version: '1.0.0',
      signTransaction: this.signTransaction,
    },
    'aptos:signMessage': {
      version: '1.0.0',
      signMessage: this.signMessage,
    },
    'aptos:onAccountChange': {
      version: '1.0.0',
      onAccountChange: this.onAccountChange,
    },
    'aptos:onNetworkChange': {
      version: '1.0.0',
      onNetworkChange: this.onNetworkChange,
    },
    'aptos:account': {
      version: '1.0.0',
      account: this.account,
    },
    'aptos:signAndSubmitTransaction': {
      version: '1.1.0',
      signAndSubmitTransaction: this.signAndSubmitTransaction,
    },
  };
}
